import React, { useEffect, useRef } from "react";
import { Link } from "gatsby";
import { Popover, PopoverButton, PopoverBackdrop, PopoverPanel } from "@headlessui/react";
import clsx from "clsx";
import { StaticImage } from "gatsby-plugin-image";
import { Container } from "@/components/Container";
import { CloseIcon, ChevronDownIcon, SunIcon } from "@/components/Icons";
import { GitHubLink, LinkedInLink } from "./SocialLink";
function MobileNavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <PopoverButton as={Link} to={to} className="block py-2">
        {children}
      </PopoverButton>
    </li>
  );
}

function MobileNavigation(props: React.ComponentPropsWithoutRef<typeof Popover>) {
  return (
    <Popover {...props}>
      <PopoverButton className="group flex items-center rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-zinc-800 shadow-lg shadow-zinc-800/5 ring-1 ring-zinc-900/5 backdrop-blur">
        Menu
        <ChevronDownIcon className="ml-3 h-auto w-2 stroke-zinc-500 group-hover:stroke-zinc-700" />
      </PopoverButton>
      <PopoverBackdrop
        transition
        className="fixed inset-0 z-50 bg-zinc-800/40 backdrop-blur-sm duration-150 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <PopoverPanel
        focus
        transition
        className="fixed inset-x-4 top-8 z-50 origin-top rounded-3xl bg-white p-8 ring-1 ring-zinc-900/5 duration-150 data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="flex flex-row-reverse items-center justify-between">
          <PopoverButton aria-label="Close menu" className="-m-1 p-1">
            <CloseIcon className="h-6 w-6 text-zinc-500" />
          </PopoverButton>
          <h2 className="text-sm font-medium text-zinc-600">Navigation</h2>
        </div>
        <nav className="mt-6">
          <ul className="-my-2 divide-y divide-zinc-100 text-base text-zinc-800">
            <MobileNavItem to="/about">About</MobileNavItem>
            <MobileNavItem to="/articles">Articles</MobileNavItem>
            <MobileNavItem to="/favorites">Favorites</MobileNavItem>
          </ul>
        </nav>
      </PopoverPanel>
    </Popover>
  );
}

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  const defaultClasses = "relative block px-3 py-2 transition";
  const inActiveClasses = "hover:text-teal-500";
  const activeClasses = "text-teal-500";
  return (
    <li>
      <Link
        to={to}
        className={clsx(defaultClasses, inActiveClasses)}
        activeClassName={clsx(defaultClasses, activeClasses)}
      >
        {children}
      </Link>
    </li>
  );
}

function DesktopNavigation(props: React.ComponentPropsWithoutRef<"nav">) {
  return (
    <nav {...props}>
      <ul className="flex rounded-full bg-white/90 px-3 text-sm font-medium text-zinc-800 shadow-lg shadow-zinc-800/5 ring-1 ring-zinc-900/5 backdrop-blur">
        <NavItem to="/about">About</NavItem>
        <NavItem to="/articles">Articles</NavItem>
        <NavItem to="/favorites">Favorites</NavItem>
      </ul>
    </nav>
  );
}

function ThemeToggle() {
  return (
    <button
      type="button"
      aria-label="Toggle theme"
      className="group rounded-full bg-white/90 px-3 py-2 shadow-lg shadow-zinc-800/5 ring-1 ring-zinc-900/5 backdrop-blur transition"
    >
      <SunIcon className="h-6 w-6 fill-zinc-100 stroke-zinc-500 transition group-hover:fill-zinc-200 group-hover:stroke-zinc-700" />
    </button>
  );
}

function clamp(number: number, a: number, b: number) {
  let min = Math.min(a, b);
  let max = Math.max(a, b);
  return Math.min(Math.max(number, min), max);
}

function AvatarContainer({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={clsx(
        className,
        "h-10 w-10 rounded-full bg-white/90 p-0.5 shadow-lg shadow-zinc-800/5 ring-1 ring-zinc-900/5 backdrop-blur"
      )}
      {...props}
    />
  );
}

function Avatar({
  large = false,
  className,
  ...props
}: Omit<React.ComponentPropsWithoutRef<typeof Link>, "to"> & {
  large?: boolean;
}) {
  return (
    <Link to="/" aria-label="Home" className={clsx(className, "pointer-events-auto")} {...props}>
      <StaticImage
        src="../images/profile.jpg"
        alt="Profile picture"
        width={40}
        height={40}
        quality={100}
        className={clsx("rounded-full bg-zinc-100 object-cover h-9 w-9")}
      />
    </Link>
  );
}

export function Header() {
  return (
    <>
      <header
        className="Header pointer-events-none relative z-50 flex flex-none flex-col"
        style={{
          height: "var(--header-height)",
          marginBottom: "var(--header-mb)",
        }}
      >
        <div
          className="top-0 z-10 h-16 pt-6"
          style={{
            position: "var(--header-position)" as React.CSSProperties["position"],
          }}
        >
          <Container
            className="top-[var(--header-top,theme(spacing.6))] w-full"
            style={{
              position: "var(--header-inner-position)" as React.CSSProperties["position"],
            }}
          >
            <div className="relative flex gap-4 items-center">
              <div className="flex flex-1">
                <AvatarContainer>
                  <Avatar />
                </AvatarContainer>
              </div>
              <div className="flex flex-1 justify-end md:justify-center">
                <MobileNavigation className="pointer-events-auto md:hidden" />
                <DesktopNavigation className="pointer-events-auto hidden md:block" />
              </div>
              <div className="flex justify-end gap-md md:flex-1 pointer-events-auto">
                <GitHubLink />

                <LinkedInLink />
              </div>
            </div>
          </Container>
        </div>
      </header>
    </>
  );
}
